// @ts-check
import React, { useCallback, useState, useMemo } from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAPI from 'services/useAPI';
import { useTranslation } from 'localization/useTranslation';
import useAlerts from 'utils/hooks/useAlerts';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import createQueryRequest from 'services/createQueryRequest';

import DeleteAsset from './DeleteAsset';

import Tl from 'localization/LocalizationKeys';
import { assetKeys } from './queries';
import { providerKeys } from '../Providers/queries';
import { getProviderClientListFromAsset } from 'utils/asset';

import { useUserPermissionsQueries } from 'services/Permissions/queries';
import { useAuthService } from 'services/AuthService/AuthService';
import useDeferredValue from 'utils/hooks/useDeferredValue';
import { search } from 'components/Common/utils/collection';

/**
 * @typedef {import('utils/types').AssetType} AssetType
 */

/**
 * @typedef {import('utils/types').Provider} Provider
 */

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').SortByType<AssetType>}
 * @constant
 */
const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};

/**
 * @typedef {Object} ProviderColumnProps
 * @property {Provider[]} providers
 */

/**
 * @param {ProviderColumnProps} props
 * @returns {JSX.Element}
 */
const ProviderColumn = ({ providers }) => {
  const T = useTranslation();
  if (!_.isEmpty(providers)) {
    return <>{ _.join(_.map(providers, provider => provider.displayName), ', ') }</>;
  }
  return <>{ T.common.noDataSource }</>;
};


/**
 * @type {import('components/Common/SimpleTable/SimpleTable').ColumnType<AssetType>[]}
 */
const columns = [
  {
    label: Tl.common.name,
    path: 'displayName',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.id,
    path: 'id',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.model,
    path: 'modelId',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.dataProvider,
    path: 'providers',
    isSortable: true,
    searchItem: true,
    render: (providers) => {
      return <ProviderColumn providers={providers} />;
    }
  }
];

/**
 * @typedef {Object} AdminAssetListProps
 * @property {Array} breadcrumbs
 */

/**
 * Administrate a list of assets in the system
 * @param {AdminAssetListProps} props
 * @returns {JSX.Element}
 */
const AdminAssetList = ({ breadcrumbs }) => {
  const history = useHistory();
  const T = useTranslation();
  const api = useAPI();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [alerts, addSuccessAlert] = useAlerts();
  const { user } = useAuthService();
  const [perPage, setPerPage] = React.useState(20);
  const [page, setPage] = React.useState(1);
  const [startIdx, setStartIdx] = React.useState(0);
  const [endIdx, setEndIdx] = React.useState(20);
  const [nonDeferredSearchText, setSearchText] = useState('');
  const searchText = useDeferredValue(nonDeferredSearchText);

  const assetQuery = useQuery(assetKeys.assets(), createQueryRequest(api.core.getAssets), {
    retry: 1
  });
  const providerQuery = useQuery(providerKeys.providers(), createQueryRequest(api.core.getProviders), {
    retry: 1
  });

  const isLoading = providerQuery.isLoading || assetQuery.isLoading;
  const hasError = providerQuery.isError || assetQuery.isError;

  const assets = useMemo(/** @return {AssetType[]}*/ () => {
    const assetList = _.map(/** @type {AssetType[]} */(assetQuery.data), asset => {
      const p = getProviderClientListFromAsset(asset);
      const mappedProviders = _.map(p, providerClient => providerClient.providerId);
      const uniq = _.uniq(mappedProviders);
      const foundProviders = _.map(uniq, provider => {
        let found = _.find(/** @type {array}*/(providerQuery.data), { id: provider });
        if (!found) {
          found = _.find(/** @type {AssetType[]}*/(assetQuery.data), { id: provider });
        }
        return found || { displayName: '' };
      });
      return { ...asset, providers: foundProviders };
    });
    const searched = search(assetList, searchText, 'displayName', 'id', 'modelId', 'providers');
    return _.orderBy(searched, item => {
      const itemValue = _.get(item, DefaultSortBy.path);
      if (_.isString(itemValue)) {
        return itemValue?.toLowerCase();
      }
      return itemValue;
    }, [/** @type {'desc'|'asc'} */(DefaultSortBy.direction)]);
  }, [assetQuery.data, providerQuery.data, searchText]);

  const onNewAsset = useCallback(() => {
    history.push('/admin/assets/new-asset');
  }, [history]);

  const onViewAsset = useCallback((/** @type {AssetType} */asset) => {
    history.push(`assets/${asset.id}`);
  }, [history]);

  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const onDelete = useCallback(asset => setConfirmDeleteItem(asset), []);

  const onMigrateToModel = useCallback((item) => {
    history.push({ pathname: `assets/${item.id}/migrate` });
  }, [history]);

  const disableDelete = useCallback((item) => {
    return (item.hideDelete === true);
  }, []);

  const disableMigrateToModel = useCallback((item) => {
    return (item.hideMigrateToModel === true);
  }, []);

  const actions = useMemo(() => {
    return [
      {
        title: T.common.delete,
        onClick: onDelete,
        isDisabled: disableDelete
      },
      {
        title: T.migrate.asset,
        onClick: onMigrateToModel,
        isDisabled: disableMigrateToModel
      }

    ];
  }, [T.common.delete, onDelete, disableDelete, T.migrate.asset, onMigrateToModel, disableMigrateToModel]);

  const deletedAsset = useCallback(() => {
    setConfirmDeleteItem(null);
    addSuccessAlert(T.common.request.delete.success);
  }, [T.common.request.delete.success, addSuccessAlert]);

  const visibleAssets = useMemo(() => {
    return _.slice(assets, startIdx, endIdx);
  }, [assets, startIdx, endIdx]);

  const visibleAssetsIds = _.map(visibleAssets, asset => asset.id);

  const deleteAssetsPermissionsQueries = useUserPermissionsQueries(user?.userId, 'asset', visibleAssetsIds, 'delete');
  const deleteAssetsPermissionsData = useMemo(() => {
    return _.map(deleteAssetsPermissionsQueries, res => res?.data?.[0]);
  }, [deleteAssetsPermissionsQueries]);

  const mergedAssets = useMemo(() => {
    return _.map(visibleAssets, asset => {
      const isDeleteAllowed = _.find(deleteAssetsPermissionsData, item => item?.resourceId === asset.id)?.result ?? false;
      return { ...asset, hideDelete: !isDeleteAllowed, hideMigrateToModel: !isDeleteAllowed };
    });
  }, [deleteAssetsPermissionsData, visibleAssets]);

  const onSetPage = useCallback((_event, newPage, unused, startIdx, endIdx) => {
    setPage(newPage);
    setStartIdx(startIdx);
    setEndIdx(endIdx);
  }, []);

  const onPerPageSelect = useCallback((_event, newPerPage, newPage, startIdx, endIdx) => {
    setPerPage(newPerPage);
    setPage(newPage);
    setStartIdx(startIdx);
    setEndIdx(endIdx);
  }, []);

  const pageInfo = useMemo(() => ({
    page,
    perPage,
    itemCount: assets.length,
    onSetPage,
    onPerPageSelect
  }), [assets.length, onPerPageSelect, onSetPage, page, perPage]);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/assets/list'}>
      { alerts }

      <SimpleTable
        isLoading={isLoading && _.isEmpty(assets)}
        onRowClick={onViewAsset}
        data={mergedAssets}
        columns={columns}
        sortBy={DefaultSortBy}
        hasError={hasError}
        errorText={T.common.error.failed.loading.list}
        compact
        globalActions={[{ onClick: onNewAsset }]}
        actions={actions}
        pageInfo={pageInfo}
        onSearch={setSearchText}
      />
      <DeleteAsset
        confirmDeleteItem={confirmDeleteItem}
        onDelete={deletedAsset}
        cancelDelete={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminAssetList;
